/* Notification Center — demo data.
 *
 * AI-only design module (no ALPACA NavigationView, no backend yet), exactly like
 * Performance Analytics.  The data here mirrors the standalone notification-center
 * prototype 1:1 so the React module shows the same rules / categories / recipients
 * / templates / deliveries.  When the real API lands this file is replaced by a
 * reader hook — the components consume these typed shapes either way. */
import type { NcHoldTimeTarget } from './holdTime';

export type Severity = 'critical' | 'warning' | 'info';
export type Purpose = 'internal' | 'external' | 'violation' | 'reminder' | 'system' | 'escalation';
export type DeliveryStatus = 'Delivered' | 'Read' | 'Sent' | 'Failed' | 'Bounced';

export const PURPOSE_LABEL: Record<Purpose, string> = {
  internal: 'Internal Alert',
  external: 'External Communication',
  violation: 'Compliance Violation',
  reminder: 'Workflow Reminder',
  system: 'System Failure',
  escalation: 'Escalation',
};

export const SEVERITY_LABEL: Record<Severity, string> = { critical: 'Critical', warning: 'Warning', info: 'Info' };

export type RecipientKind = 'role' | 'dynamic' | 'external';
export const RECIPIENT_KIND_LABEL: Record<RecipientKind, string> = { role: 'Role', dynamic: 'Dynamic', external: 'External' };

/** One notification recipient on a rule (a role, a dynamic source, or an
 *  external party) with its per-channel selection. Persisted in the rule's
 *  config blob (config.recipients) so add/remove/channel edits round-trip. */
export interface NcRecipient { id: string; name: string; kind: RecipientKind; desc?: string; department?: string; inApp: boolean; email: boolean; sms?: boolean; }

/** One persisted change-history entry for a rule (config.history). Written by
 *  the Rule Detail Save from the actual diff — never seeded/mocked. */
export interface NcAuditEntry { at: string; user: string; action: string; field?: string; oldValue?: string; newValue?: string; }

/** Typed view of the rule's opaque config JSON blob. */
export interface NcRuleConfig { recipients?: NcRecipient[]; recipientGroups?: string[]; emailTemplate?: string; history?: NcAuditEntry[]; [k: string]: unknown; }

/** Options for the "Add recipient" combo — roles + dynamic/external sources. */
export const RECIPIENT_CATALOG: { name: string; kind: RecipientKind; desc: string }[] = [
  { name: 'Lab Director', kind: 'role', desc: 'Resolves to current Lab Director' },
  { name: 'QA Manager', kind: 'role', desc: 'Resolves to current QA Manager' },
  { name: 'Lab Manager', kind: 'role', desc: 'Resolves to current Lab Manager' },
  { name: 'Sample Receiving', kind: 'role', desc: 'Sample Receiving team' },
  { name: 'Project Manager', kind: 'role', desc: 'Resolves to the job Project Manager' },
  { name: 'Analyst', kind: 'role', desc: 'Assigned analyst on the record' },
  { name: 'Reviewer', kind: 'role', desc: 'Secondary reviewer' },
  { name: 'Account Owner', kind: 'role', desc: 'Client account owner' },
  { name: 'Billing', kind: 'role', desc: 'Billing / AR team' },
  { name: 'Compliance', kind: 'role', desc: 'Compliance officer' },
  { name: 'IT', kind: 'role', desc: 'IT / systems team' },
  { name: 'Data Team', kind: 'role', desc: 'Data / EDD team' },
  { name: 'Maintenance', kind: 'role', desc: 'Instrument maintenance' },
  { name: 'Assigned Salesperson', kind: 'dynamic', desc: 'Client.AssignedSalesperson on the source record' },
  { name: 'Client contact', kind: 'external', desc: 'External client contact on the source record' },
];

/** Parse a rule's opaque config blob into the typed shape (safe on bad JSON). */
export function parseRuleConfig(rule: NcRule): NcRuleConfig {
  if (!rule.config) return {};
  try { const o = JSON.parse(rule.config) as unknown; return (o && typeof o === 'object') ? o as NcRuleConfig : {}; } catch { return {}; }
}

/** Recipients for a rule: the saved structured list (config.recipients) if
 *  present, else derived once from the legacy display string so an existing
 *  rule "loads from the saved rule". */
/** The selected Recipient GROUP ids on a rule. Rules are linked to groups by the
 *  stable id (rule.recipientGroups, from the junction), so a rename never breaks
 *  the link. Legacy fallback only: a rule with no id refs is matched from its
 *  config.recipientGroups NAMES against the live groups. */
export function recipientGroupIdsOf(rule: NcRule, groups: NcRecipientGroup[]): string[] {
  if (rule.recipientGroups) return rule.recipientGroups.map((g) => g.id);
  const cfg = parseRuleConfig(rule);
  const names = Array.isArray(cfg.recipientGroups) ? cfg.recipientGroups.filter((n): n is string => typeof n === 'string') : [];
  return names.flatMap((n) => { const id = groups.find((g) => g.name === n)?.id; return id ? [id] : []; });
}

/** The rule's linked Email Template id. Rules reference a template by its stable
 *  id (rule.emailTemplateId = AI_NotificationRule.EmailTemplateId), so a rename
 *  never breaks the link. Legacy fallback only: a rule with no id is matched from
 *  its config.emailTemplate NAME against the live templates. */
export function emailTemplateIdOf(rule: NcRule, templates: NcTemplate[]): string | null {
  if (rule.emailTemplateId) return rule.emailTemplateId;
  const name = parseRuleConfig(rule).emailTemplate;
  return (name && templates.find((t) => t.name === name)?.id) || null;
}

/** Build the rule's id-based group refs from the selected ids, taking each name
 *  from the LIVE groups (else the rule's last-known name for a deleted group). */
export function groupRefsFor(ids: string[], groups: NcRecipientGroup[], known: NcRuleGroupRef[] = []): NcRuleGroupRef[] {
  return ids.map((id) => {
    const live = groups.find((g) => g.id === id);
    return live ? { id, name: live.name, exists: true } : { id, name: known.find((k) => k.id === id)?.name ?? '', exists: false };
  });
}

export function recipientsOf(rule: NcRule): NcRecipient[] {
  const cfg = parseRuleConfig(rule);
  if (cfg.recipients && cfg.recipients.length) return cfg.recipients;
  const raw = (rule.recipients ?? '').trim();
  if (!raw || raw === '—') return [];
  return raw.split(',').map((part, i) => {
    const name = part.trim().replace(/\s*\((external|dynamic|role)\)\s*/i, '').trim();
    const external = !!rule.externalRecipient || /external/i.test(part);
    const dynamic = /dynamic/i.test(part);
    const kind: RecipientKind = external ? 'external' : dynamic ? 'dynamic' : 'role';
    const found = RECIPIENT_CATALOG.find((c) => c.name.toLowerCase() === name.toLowerCase());
    return { id: `r${i}-${name}`, name, kind, desc: found?.desc ?? '', inApp: !external, email: true };
  });
}

/** Join a recipient list into the display string kept on rule.recipients. */
export function recipientsToDisplay(list: NcRecipient[]): string {
  return list.length ? list.map((r) => r.name).join(', ') : '';
}

export interface NcRule {
  name: string;
  code: string;
  purpose: Purpose;
  /** Alert severity — omitted for outbound communications. */
  severity?: Severity;
  /** Outbound communication (to an external party) rather than an internal alert. */
  outbound?: boolean;
  /** Per-target monitoring count, e.g. "5 targets" — omitted when none. */
  targets?: string;
  /** Permit Library target keys (permitLibrary.ts) this rule has ENABLED. Set by
   *  the wizard so Edit (Rule Detail) reloads the exact ON/OFF selection. When
   *  absent (legacy rules) the detail defaults every parameter ON. */
  targetKeys?: string[];
  /** Trigger form/event ids (triggerForms.ts) that evaluate & fire this rule —
   *  e.g. ['sdms','result-validation']. The rule fires ONCE per record even if
   *  it passes several of them (duplicate firing suppressed). Omitted on legacy
   *  rules; the UI falls back to the default result-validation trigger and shows
   *  it, never silently. A saved id later disabled in Default Settings is kept
   *  and flagged, never dropped. */
  triggers?: string[];
  /** Persistence order within its category (dbo.AI_NotificationRule.SortOrder) —
   *  round-tripped so an edit/toggle never reorders the row. */
  sortOrder?: number;
  /** Opaque JSON config blob (delivery/escalation/template) — reserved; carried
   *  through so a future settings-save round-trips without losing it. */
  config?: string;
  recipients: string;
  /** The rule's Recipient Groups, linked by the STABLE group id (Oid). The name
   *  is resolved live server-side, so a renamed group shows its new name here;
   *  `exists:false` = the group was deleted. Absent only on a rule not yet saved
   *  through the id-based path (legacy) — see recipientGroupIdsOf. */
  recipientGroups?: NcRuleGroupRef[];
  /** The rule's Email Template, linked by its STABLE id (AI_NotificationRule.
   *  EmailTemplateId) — a reference, never a copy. null/absent = no template (the
   *  built-in email is sent). The name shown is always the template's CURRENT
   *  name, looked up by id. */
  emailTemplateId?: string | null;
  /** The pop-up setting this rule's in-app alerts use (Notification Center → Pop-up Settings);
   *  null = follows the Default. When SAVING, '' means "the user just chose the Default" (clears
   *  the link) while null/absent leaves the stored link untouched. */
  popupSettingId?: string | null;
  /** Whether the recipient is an external party (renders differently). */
  externalRecipient?: boolean;
  ack?: boolean;
  enabled: boolean;
  /** Delivery Method → In-App (bell): the dispatcher puts the alert in the bell / pop-up of
   *  every user whose recipient-group member row has the In App tick. Saved; default off. */
  notifyInApp?: boolean;
  /** Delivery Method → Email. Saved; default on. Undefined is sent as "not supplied", so the
   *  server keeps the stored value rather than switching email off. */
  notifyEmail?: boolean;
  /** Delivery Method → Auto-resolve when condition clears: a sent alert is marked Resolved once
   *  everything it covers no longer meets the condition. Saved; default off. */
  autoResolve?: boolean;
  /** Delivery Method → Delay (AI_NotificationRule.DelayMode): Immediate · After1Hour · DailyDigest. Saved;
   *  default Immediate. */
  delayMode?: NcDelayMode;
  /** Delivery Method → Business hours only (Mon–Fri 08:00–17:00, not on lab holidays). Saved; default off. */
  businessHoursOnly?: boolean;
  /** Delivery Method → Suppress duplicate firing (permit-limit exceedances). Saved; default on. */
  suppressDuplicates?: boolean;
  /** Delivery Method → Client visible (portal). Saved; default off — there is no client portal yet. */
  clientVisible?: boolean;
  /** Delivery Method → In-App alert → Repeat every N Hours/Minutes until acknowledged. Saved; default off ·
   *  4 · Hours (the number and unit are kept while it is off). */
  repeatEnabled?: boolean;
  repeatEvery?: number;
  repeatUnit?: 'Hours' | 'Minutes';
  /** Escalation → Escalate to: the Recipient Group an unacknowledged alert is escalated to. null / absent =
   *  no escalation. When SAVING, '' means "the user chose no escalation" (clears it); null/absent = untouched. */
  escalationGroupId?: string | null;
  /** Escalation → "also send it to": the individual users (PermissionPolicyUser Oids) an unacknowledged alert is
   *  escalated to (2026-10-24 — replaces the group for new settings). When SAVING, absent = untouched; a list
   *  replaces the stored one (and, when non-empty, clears escalationGroupId). */
  escalationUserIds?: string[];
  /** The rule row's Oid (read-only) — the audit Source for the shared Audit chip. */
  oid?: string;
  /** Escalation → Escalate after. Saved; default 1h. */
  escalateAfter?: NcEscalateAfter;
  /** Hold Time category only — the rule's Test Method targets (Alert flag + Warn
   *  Before hours), stored relationally. Absent on every other rule; a save that
   *  leaves it undefined does not touch the stored set (holdTime.ts). */
  holdTimeTargets?: NcHoldTimeTarget[];
}

export interface NcCategory {
  /** Stored sequence number — the key rules link to (0 = not saved yet). */
  code: number;
  /** The Category Code shown to users (RC-01, RC-02 …), formatted by the API. */
  codeLabel?: string;
  name: string;
  icon: string;
  order: number;
  active: boolean;
  rules: NcRule[];
}

/** A rule → Recipient Group link. `id` is the identity; `name` is display only. */
export interface NcRuleGroupRef { id: string; name: string; exists: boolean; }

export interface NcRecipientGroup {
  /** Stable group id (dbo.AI_NotificationRecipientGroup.Oid) — what rules link to.
   *  Absent only for a group created this session until its save returns the id. */
  id?: string;
  name: string;
  members: string;
  channels: string[];
  usedBy: number;
  /** Structured members (Role/Department/channels) persisted to the DB member
   *  table. Present once a group round-trips; `members`/`channels` are derived
   *  from it for the list. Absent on a legacy display-only group. */
  recipients?: NcRecipient[];
  /** Individually selected USERS (Members → Users tab), linked by the existing user
   *  id. Name / roles / department are read live from the Users data source. */
  users?: NcGroupUser[];
}

/** One individually selected user of a Recipient Group. `userId` is the identity
 *  (PermissionPolicyUser.Oid); `userName` / `roles` / `department` are display only,
 *  resolved live by the server. `exists:false` = that user has since been deleted. */
export interface NcGroupUser {
  userId: string;
  /** The login (PermissionPolicyUser.UserName). */
  userName: string;
  /** The person's name as the Users form shows it — what the screens display. */
  name?: string;
  roles?: string;
  department?: string;
  inApp: boolean;
  email: boolean;
  sms: boolean;
  exists?: boolean;
}

/** A selected user's display name: their name, else their login. */
export function groupUserName(u: { name?: string | null; userName?: string | null }): string {
  return u.name?.trim() || u.userName?.trim() || 'Unknown user';
}

/** The display string kept on a group (`members`): its roles, then its users. */
export function groupMembersDisplay(recipients: NcRecipient[], users: NcGroupUser[] = []): string {
  return [...recipients.map((r) => r.name), ...users.map((u) => groupUserName(u))].join(', ');
}

/** The union of channels used by a group's roles AND users — the list badges. */
export function groupChannels(recipients: NcRecipient[], users: NcGroupUser[] = []): string[] {
  const all = [...recipients.map((r) => ({ inApp: r.inApp, email: r.email, sms: !!r.sms })), ...users];
  const out: string[] = [];
  if (all.some((r) => r.inApp)) out.push('IN-APP');
  if (all.some((r) => r.email)) out.push('EMAIL');
  if (all.some((r) => r.sms)) out.push('SMS');
  return out;
}

export interface NcTemplate {
  /** Stable template id (dbo.AI_NotificationTemplate.Oid) — what a rule links to
   *  (EmailTemplateId). Absent only for a template created this session until its
   *  save returns the id. */
  id?: string;
  name: string;
  category: string;
  subject: string;
  body?: string;
  active: boolean;
  /** DB list order (from AI_NotificationTemplate.SortOrder); preserved on edit,
   *  max+10 on add. Absent on a form-built template until it round-trips. */
  sortOrder?: number;
}

export interface NcDelivery {
  /** SentAt from the DB (raw wire value; render via fmtLocalShort). */
  time: string;
  rule: string;
  subject: string;
  channel: string;
  /** Recipient role / group role. */
  role: string;
  recipient: string;
  status: DeliveryStatus;
  detail: string;
  /** The row's id — what Resend / Send Again act on. */
  id?: string;
  /** The Actions column, decided by the API: Resend (not delivered) · SendAgain (Sent) · none. */
  action?: DeliveryAction | null;
  /** Why there is no action (the disabled button's tooltip). */
  actionNote?: string;
  /** Set on a re-send row: 'Resend' · 'Send Again'. */
  attemptType?: string;
  /** Who made the re-send. */
  attemptedBy?: string;
  /** When the record it was re-sent from was logged (raw wire value). */
  originalTime?: string;
  /** In-App rows: whether the alert needed an acknowledgment. */
  ackRequired?: boolean;
  /** In-App rows: who acknowledged it (the recipient); empty until acknowledged. */
  acknowledgedBy?: string;
  /** In-App rows: when it was acknowledged (raw wire value); empty until then. */
  acknowledgedAt?: string;
  /** When the alert this row delivered auto-resolved (raw wire value); empty while open. */
  resolvedAt?: string;
}

/** The stored "Escalate after" values (EscalationPolicy on the server). */
// 'EOD' (End of business day) was retired 2026-10-24 — a rule still storing it is read as '4h' (api.ts).
export type NcEscalateAfter = '30m' | '1h' | '4h';

/** The stored Delay values (DeliveryScheduler on the server). */
export type NcDelayMode = 'Immediate' | 'After1Hour' | 'DailyDigest';

/** A Delivery Log row action (RTCFR 2026-10-16). */
export type DeliveryAction = 'Resend' | 'SendAgain';

export const NC_CATEGORIES: NcCategory[] = [
  {
    code: 1, name: 'Result Exceedance', icon: 'ti-droplet-exclamation', order: 10, active: true,
    rules: [
      { name: 'Permit Limit Exceedance', code: 'EXC-PERMIT-01', purpose: 'violation', severity: 'critical', targets: '5 targets', triggers: ['sdms', 'result-validation', 'result-approval'], recipients: 'Lab Director, QA Manager', ack: true, enabled: true },
      // one trigger ('sdms-validation') is DISABLED in Default Settings — kept here
      // to exercise the safe-handling path (Rule Detail shows it flagged, never drops it).
      { name: 'DW Micro Positive — Confirmed Repeat', code: 'EXC-MICRO-DW-02', purpose: 'violation', severity: 'critical', targets: '2 targets', triggers: ['sdms-validation', 'result-approval'], recipients: 'Lab Director, QA Manager', ack: true, enabled: true },
    ],
  },
  {
    code: 2, name: 'Sample Receipt', icon: 'ti-package-import', order: 20, active: true,
    rules: [
      { name: 'Cooler / Sample Temperature Out of Range', code: 'RCPT-TEMP-001', purpose: 'violation', severity: 'critical', targets: '1 target', recipients: 'Sample Receiving, QA Manager', enabled: true },
      { name: 'Preservation Failure (pH / preservative)', code: 'RCPT-PRES-002', purpose: 'violation', severity: 'critical', targets: '2 targets', recipients: 'Sample Receiving, QA Manager', enabled: true },
      { name: 'Insufficient Sample Volume', code: 'RCPT-VOL-003', purpose: 'reminder', severity: 'warning', recipients: 'Sample Receiving, Project Manager', enabled: true },
      { name: 'Broken / Leaking Container', code: 'RCPT-CONT-004', purpose: 'reminder', severity: 'warning', recipients: 'Sample Receiving', enabled: true },
      { name: 'COC Discrepancy', code: 'RCPT-COC-005', purpose: 'reminder', severity: 'warning', recipients: 'Sample Receiving, Project Manager', enabled: true },
      { name: 'Field QC Missing (trip / field blank)', code: 'RCPT-FQC-006', purpose: 'reminder', severity: 'info', recipients: 'Project Manager', enabled: false },
      { name: 'Received Past Hold Time', code: 'RCPT-HT-007', purpose: 'violation', severity: 'critical', recipients: 'QA Manager, Project Manager', ack: true, enabled: true },
    ],
  },
  {
    code: 3, name: 'Compliance & TAT', icon: 'ti-clipboard-check', order: 30, active: true,
    rules: [
      { name: 'Holding Time Violation', code: 'CMPL-HT-03', purpose: 'violation', severity: 'critical', targets: '6 targets', recipients: 'Analyst, QA Manager', ack: true, enabled: true },
      { name: 'Extraction / Prep Hold Time Approaching', code: 'TAT-XHOLD-010', purpose: 'reminder', severity: 'warning', targets: '4 targets', recipients: 'Analyst', enabled: true },
      { name: 'Analysis Hold Time Approaching', code: 'TAT-AHOLD-011', purpose: 'reminder', severity: 'warning', targets: '6 targets', recipients: 'Analyst, Lab Manager', enabled: true },
      { name: 'Sample Due Warning', code: 'TAT-DUE-012', purpose: 'reminder', severity: 'warning', targets: '1 target', recipients: 'Lab Manager', enabled: true },
      { name: 'Sample Past Due', code: 'TAT-PASTDUE-013', purpose: 'escalation', severity: 'critical', targets: '1 target', recipients: 'Lab Manager, Account Owner', ack: true, enabled: true },
      { name: 'Calibration Expiring (7 days)', code: 'CMPL-CAL-WARN', purpose: 'reminder', severity: 'warning', targets: '1 target', recipients: 'QA Manager', enabled: true },
      { name: 'Accreditation / Certification Expiring', code: 'CERT-EXP-014', purpose: 'violation', severity: 'warning', recipients: 'QA Manager', enabled: true },
      { name: 'PT / PE Sample Due', code: 'CERT-PT-015', purpose: 'violation', severity: 'warning', recipients: 'QA Manager', enabled: true },
      { name: 'PT Failure', code: 'CERT-PTFAIL-016', purpose: 'violation', severity: 'critical', recipients: 'QA Manager, Lab Director', ack: true, enabled: true },
      { name: 'DMR / Regulatory Report Deadline', code: 'CMPL-DMR-017', purpose: 'violation', severity: 'warning', recipients: 'Lab Manager, Compliance', enabled: true },
      { name: 'CAPA / NCR Overdue', code: 'CMPL-CAPA-018', purpose: 'violation', severity: 'warning', recipients: 'QA Manager', enabled: false },
    ],
  },
  {
    code: 4, name: 'QC & Workflow', icon: 'ti-flask', order: 40, active: true,
    rules: [
      { name: 'QC Batch Failure (CCV / LCS)', code: 'QC-FAIL-02', purpose: 'reminder', severity: 'critical', targets: '3 targets', recipients: 'Analyst, QA Manager', enabled: true },
      { name: 'Method Blank Contamination', code: 'QC-MB-020', purpose: 'reminder', severity: 'warning', targets: '1 target', recipients: 'Analyst, QA Manager', enabled: true },
      { name: 'Surrogate Recovery Out of Limits', code: 'QC-SUR-021', purpose: 'reminder', severity: 'warning', targets: '2 targets', recipients: 'Analyst', enabled: true },
      { name: 'LCS / LCSD Out of Control', code: 'QC-LCS-022', purpose: 'reminder', severity: 'critical', targets: '2 targets', recipients: 'Analyst, QA Manager', enabled: true },
      { name: 'MS / MSD Recovery or RPD Failure', code: 'QC-MSD-023', purpose: 'reminder', severity: 'warning', targets: '2 targets', recipients: 'Analyst', enabled: true },
      { name: 'Control Chart Out of Control', code: 'QC-CHART-024', purpose: 'reminder', severity: 'warning', recipients: 'QA Manager', enabled: false },
      { name: 'Result Over Calibration Range (dilution)', code: 'QC-RANGE-025', purpose: 'reminder', severity: 'warning', recipients: 'Analyst', enabled: true },
      { name: 'Standard / Reagent Expiration', code: 'QC-REAG-026', purpose: 'reminder', severity: 'warning', recipients: 'Analyst, QA Manager', enabled: true },
      { name: 'Sample Stuck in Status', code: 'QC-STUCK-01', purpose: 'reminder', severity: 'warning', targets: '1 target', recipients: 'Lab Manager', enabled: true },
      { name: 'Secondary Review / Approval Pending', code: 'QC-REVIEW-027', purpose: 'reminder', severity: 'info', recipients: 'Reviewer', enabled: false },
    ],
  },
  {
    code: 5, name: 'Client & Sales', icon: 'ti-users', order: 50, active: true,
    rules: [
      { name: 'Sample Receipt Confirmation → Client', code: 'CLNT-RCPT-030', purpose: 'external', outbound: true, recipients: 'Client contact (external)', externalRecipient: true, enabled: true },
      { name: 'Report Ready / Delivered → Client', code: 'CLNT-RPT-031', purpose: 'external', outbound: true, recipients: 'Client contact (external)', externalRecipient: true, enabled: true },
      { name: 'Client At-Risk Cadence Gap', code: 'CLNT-CADENCE-01', purpose: 'internal', severity: 'warning', targets: '1 target', recipients: 'Assigned Salesperson (dynamic)', enabled: true },
      { name: 'Salesperson Scorecard Below Target', code: 'CLNT-SCORE-04', purpose: 'internal', severity: 'info', recipients: 'Assigned Salesperson', enabled: false },
      { name: 'Invoice Overdue', code: 'CLNT-INV-032', purpose: 'internal', severity: 'warning', recipients: 'Account Owner, Billing', enabled: true },
    ],
  },
  {
    code: 6, name: 'Subcontract & Logistics', icon: 'ti-truck-delivery', order: 60, active: true,
    rules: [
      { name: 'Subout Order → Subcontract Lab', code: 'SUB-ORDER-040', purpose: 'external', outbound: true, recipients: 'Subcontractor lab (external)', externalRecipient: true, enabled: true },
      { name: 'Subout Result Overdue', code: 'SUB-OVERDUE-041', purpose: 'reminder', severity: 'warning', targets: '1 target', recipients: 'Project Manager, Lab Manager', enabled: true },
      { name: 'Subout Result Received (needs import)', code: 'SUB-RECV-042', purpose: 'reminder', severity: 'info', recipients: 'Project Manager', enabled: true },
      { name: 'Sampling / Courier Pickup Missed', code: 'SUB-PICKUP-043', purpose: 'reminder', severity: 'warning', recipients: 'Project Manager', enabled: false },
    ],
  },
  {
    code: 7, name: 'System & Data', icon: 'ti-server-2', order: 70, active: true,
    rules: [
      { name: 'Report Delivery Failure', code: 'SYS-RPTFAIL-050', purpose: 'system', severity: 'critical', recipients: 'Report Owner, IT', enabled: true },
      { name: 'Email Failure (SMTP bounce)', code: 'SYS-EMAIL-051', purpose: 'system', severity: 'critical', recipients: 'Sender, IT', enabled: true },
      { name: 'EDD Generation / Format Failure', code: 'SYS-EDD-052', purpose: 'system', severity: 'warning', recipients: 'Data Team, Project Manager', enabled: true },
      { name: 'SDMS / Parser Import Failure', code: 'SYS-PARSE-01', purpose: 'system', severity: 'info', recipients: 'Data Team', enabled: true },
      { name: 'Instrument Data Import Failure', code: 'SYS-IMPORT-053', purpose: 'system', severity: 'warning', recipients: 'Data Team', enabled: true },
      { name: 'Client Portal Upload Failure', code: 'SYS-PORTAL-054', purpose: 'system', severity: 'warning', recipients: 'IT, Project Manager', enabled: true },
      { name: 'Backup / Scheduled Job Failure', code: 'SYS-JOB-055', purpose: 'system', severity: 'warning', recipients: 'IT', enabled: false },
    ],
  },
  {
    code: 8, name: 'Capacity & Trend', icon: 'ti-trending-up', order: 80, active: false,
    rules: [
      { name: 'Workload Imbalance Across Analysts', code: 'CAP-WORKLOAD-01', purpose: 'internal', severity: 'info', targets: '3 targets', recipients: 'Lab Manager', enabled: false },
      { name: 'Backlog Threshold Exceeded', code: 'CAP-BACKLOG-060', purpose: 'internal', severity: 'warning', recipients: 'Lab Manager', enabled: true },
      { name: 'Instrument Out of Service / Down', code: 'CAP-INSTR-061', purpose: 'internal', severity: 'warning', recipients: 'Lab Manager, Maintenance', enabled: true },
    ],
  },
];

export const NC_RECIPIENT_GROUPS: NcRecipientGroup[] = [
  { name: 'Lab Leadership', members: 'Lab Director, QA Manager', channels: ['IN-APP', 'EMAIL'], usedBy: 9 },
  { name: 'DW Compliance Team', members: 'Lab Director, QA Manager, Compliance', channels: ['IN-APP', 'EMAIL'], usedBy: 4 },
  { name: 'Sample Receiving', members: 'Sample Receiving (role)', channels: ['IN-APP', 'EMAIL'], usedBy: 7 },
  { name: 'Account Owners (dynamic)', members: 'Client.AssignedSalesperson', channels: ['IN-APP', 'EMAIL'], usedBy: 5 },
  { name: 'IT / Data Team', members: 'IT, Data Team', channels: ['EMAIL'], usedBy: 6 },
];


// Delivery Log is now DB-backed (dbo.AI_NotificationDeliveryLog) — no mock rows
// and no mock summary counts. The Overview KPIs read real stats from the store.

/** dbo.AuditData FormName of Notification Rules changes (server-written, NotificationRuleAudit, 2026-10-24). */
export const NotificationRulesAuditForm = 'Notification Rules';
/** dbo.AuditData FormNames of the Notification Center settings forms (server-written, NotificationSettingsAudit, 2026-10-24). */
export const NotificationSettingsAuditForms = {
  categories: 'Notification Rule Categories',
  recipientGroups: 'Notification Recipient Groups',
  emailTemplates: 'Notification Email Templates',
} as const;
